import type {
  EditorField,
  EditorObject,
  EditorPage,
} from "@prototypical/editor-contract";
import type { EditorManifest } from "./manifestV3";
import { pageFile, pageLabel } from "./messaging";

// Engine manifests carry the page <title> as the label ("Books — Sections &
// Staff Picks · …") and often an empty `file`. An empty file made every rail
// button read as "already the current page" (samePage("", current) normalizes
// to home), so clicks silently returned — and title-soup labels are unusable
// in a 200px rail. Derive both from the path when the manifest is sloppy.
function railLabel(label: string | undefined, path: string): string {
  const raw = (label ?? "").trim();
  if (!raw || raw.length > 24 || /[—·|"]/.test(raw)) return pageLabel(path);
  return raw;
}

export function normalizeManifest(manifest: EditorManifest | null, values: Record<string, string>) {
  const fields: EditorField[] = manifest?.fields ?? Object.entries(values).map(([path, value]) => ({
    path, value, type: "text", page: "index.html", selector: "", label: path,
  }));
  const v2Pages = manifest && manifest.schemaVersion !== 1
    ? manifest.pages.map((page) => ({
        ...page,
        file: page.file || pageFile(page.path),
        label: railLabel(page.label, page.path),
      }))
    : [];
  const pagePaths: EditorPage[] = v2Pages.length
    ? v2Pages
    : Array.from(new Set(fields.map((field) => field.page || "index.html"))).map((path) => ({
        id: path, label: pageLabel(path), path, file: path,
      }));
  const pages: EditorPage[] = (pagePaths.length ? [...pagePaths] : [{ id: "home", label: "Home", path: "index.html" }])
    .sort((a, b) => {
      const aHome = a.route === "/" || a.path === "index.html" || a.file === "index.html";
      const bHome = b.route === "/" || b.path === "index.html" || b.file === "index.html";
      return aHome === bHome ? 0 : aHome ? -1 : 1;
    });
  const fieldsByPath = new Map(fields.map((field) => [field.path, field]));
  const fieldNode = (path: string, page: string): EditorObject | null => {
    const field = fieldsByPath.get(path);
    if (!field) return null;
    return {
      id: field.path,
      label: field.label,
      kind: field.type === "link" || field.type === "url" ? "link" : field.type,
      page,
      fieldPath: field.path,
      operations: manifest?.schemaVersion === 3 ? [] : field.operations ?? ["delete"],
    };
  };
  const objectTree = (input: EditorObject[], page: string): EditorObject[] => {
    const rawById = new Map<string, EditorObject & { effectiveParentId?: string }>();
    const collect = (objects: EditorObject[], inheritedParentId?: string) => {
      for (const object of objects) {
        if (!rawById.has(object.id)) {
          rawById.set(object.id, { ...object, effectiveParentId: object.parentId ?? inheritedParentId });
        }
        collect(object.objects ?? object.children ?? [], object.id);
      }
    };
    collect(input);
    const nodes = new Map<string, EditorObject>();
    for (const object of rawById.values()) {
      const ownedFields = new Set([
        ...(object.fields ?? []),
        ...fields.filter((field) => field.objectId === object.id).map((field) => field.path),
      ]);
      nodes.set(object.id, {
        ...object,
        page,
        operations: object.operations ?? ["delete"],
        children: [...ownedFields].map((path) => fieldNode(path, page)).filter((item): item is EditorObject => !!item),
      });
    }
    const roots: EditorObject[] = [];
    for (const object of rawById.values()) {
      const node = nodes.get(object.id)!;
      const parent = object.effectiveParentId ? nodes.get(object.effectiveParentId) : undefined;
      if (parent) parent.children = [node, ...(parent.children ?? [])];
      else roots.push(node);
    }
    return roots;
  };
  const sectionTree = (page: EditorPage): EditorObject[] => {
    const sections = page.sections ?? [];
    if (manifest?.schemaVersion === 3 && !sections.length) return fields
      .filter(field => field.pageId === page.id || field.page === page.file || field.page === page.path)
      .map(field => fieldNode(field.path, page.path)).filter((field): field is EditorObject => !!field);
    const sectionById = new Map(sections.map((section) => [section.id, section]));
    const childSections = new Map<string, typeof sections>();
    for (const section of sections) {
      if (section.parentId && sectionById.has(section.parentId)) {
        childSections.set(section.parentId, [...(childSections.get(section.parentId) ?? []), section]);
      }
    }
    const build = (section: typeof sections[number]): EditorObject => {
      const directFields = fields.filter((field) =>
        field.sectionId === section.id && !field.objectId).map((field) => field.path);
      return {
        id: section.id,
        label: section.label,
        kind: "section",
        page: page.path,
        selector: section.selector,
        parentId: section.parentId,
        operations: section.operations ?? ["move-up", "move-down", "hide", "show", "delete"],
        children: [
          ...(childSections.get(section.id) ?? []).map(build),
          ...objectTree(section.objects ?? [], page.path),
          ...directFields.map((path) => fieldNode(path, page.path)).filter((item): item is EditorObject => !!item),
        ],
      };
    };
    return sections.filter((section) => !section.parentId || !sectionById.has(section.parentId)).map(build);
  };
  const objects: EditorObject[] = manifest && manifest.schemaVersion !== 1
    ? pages.map((page) => ({
        id: `page:${page.id}`,
        label: page.label,
        kind: "page",
        page: page.path,
        children: sectionTree(page),
      }))
    : pages.map((page) => ({
        id: `page:${page.id}`,
        label: page.label,
        kind: "page",
        page: page.path,
        children: fields.filter((field) => field.page === page.path).map((field) => ({
          id: field.path,
          label: field.label,
          kind: field.tag === "a" ? "link" : "text",
          page: field.page ?? "index.html",
          fieldPath: field.path,
        })),
      }));
  return { fields, pages, objects };
}
